import React, {useEffect, useMemo, useRef, useState} from "react";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {useSelector} from "react-redux";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {addDays, errorText, faDigits, faNum, isoDay, workspacePeople} from "./meetings-labels";
import {DayPicker} from "./meetings-ui";
import "../dashboard/project-dashboard.css";
import "./project-meetings.css";

// «ایجاد جلسه» / «ویرایش جلسه»: one form for both (spec 5.4, 5.5, 8.1). Opened from the meetings tab and from the
// project header's «افزودن ← جلسه». Title, date, start / end, place or link, the person responsible («مسئول جلسه»),
// invitees and agenda topics. Invitees are searched among everyone in the workspace — someone from outside the project
// may attend (boss's feedback 2026-10-25); the owner is always invited.

const TIME_OPTIONS = Array.from({length: 96}, (_, index) => {
    const value = `${String(Math.floor(index / 4)).padStart(2, "0")}:${String((index % 4) * 15).padStart(2, "0")}`;
    return {value, label: faDigits(value)};
});

const today = () => isoDay(new Date());

// the project's people for the form: members and the owner (removed ones left out)
export function peopleFromProject(projectData) {
    const people = new Map();
    [projectData?.owner, ...(projectData?.members || [])].forEach((membership) => {
        if (!membership || ["removed", "restricted"].includes(membership.memberShipStatus)) return;
        const key = String(membership.documentId || membership.id || "");
        const member = membership.member || {};
        const name = member.fullname || [member.firstname, member.lastname].filter(Boolean).join(" ") || member.username || "";
        if (key && name && !people.has(key)) people.set(key, {key, name, member});
    });
    return [...people.values()];
}

// a person in the pickers: avatar and name
const personItem = (option) => (
    <span className="flex align-items-center gap-2">
        {option?.member && <Avatar memberData={option.member} size="normal"/>}
        <span className="text-xs">{option?.name}</span>
    </span>
);

export default function MeetingFormDialog({visible, onHide, meeting, people, knownPeople = [], onSave}) {
    const membership = useSelector((state) => state.app.workspaceMembershipData);
    const everyone = useMemo(() => workspacePeople(membership?.workspace?.workspace_memberships), [membership]);
    const meKey = String(membership?.documentId || membership?.id || "");
    const [owner, setOwner] = useState("");
    const [title, setTitle] = useState("");
    const [date, setDate] = useState("");
    const [start, setStart] = useState("10:00");
    const [end, setEnd] = useState("11:00");
    const [location, setLocation] = useState("");
    const [attendees, setAttendees] = useState([]);
    const [agenda, setAgenda] = useState([""]);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const agendaRef = useRef(null);
    const [focusTopic, setFocusTopic] = useState(null);

    useEffect(() => {
        if (!visible) return;
        setTitle(meeting?.title || "");
        setDate(meeting?.date || addDays(today(), 1));
        setStart(meeting?.start || "10:00");
        setEnd(meeting?.end || "11:00");
        setLocation(meeting?.location || "");
        const firstOwner = meeting?.owner || meKey;
        setOwner(firstOwner);
        setAttendees(meeting?.attendees?.length ? meeting.attendees : firstOwner ? [firstOwner] : []);
        setAgenda(meeting?.agenda?.length ? meeting.agenda : [""]);
        setError("");
        setSaving(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, meeting]);

    // Enter in a topic opens a new row under it and moves there
    useEffect(() => {
        if (focusTopic === null) return;
        agendaRef.current?.querySelector(`[data-topic="${focusTopic}"]`)?.focus();
        setFocusTopic(null);
    }, [focusTopic, agenda.length]);
    const addTopic = (after = agenda.length - 1) => {
        setAgenda((current) => [...current.slice(0, after + 1), "", ...current.slice(after + 1)]);
        setFocusTopic(after + 1);
    };

    // names of everyone who may show: the workspace, then people the tab knows (former members keep their name)
    const byKey = useMemo(() => {
        const map = new Map();
        [...knownPeople, ...(people || []), ...everyone].forEach((item) => map.set(item.key, item));
        return map;
    }, [knownPeople, people, everyone]);
    const projectKeys = useMemo(() => new Set((people || []).map((item) => item.key)), [people]);
    // without the project's people (the sidebar opened from «کارها») nobody is tagged «خارج از پروژه»
    const tagOutside = (people || []).length > 0;
    const candidates = everyone.filter((item) => !attendees.includes(item.key));

    const chooseOwner = (key) => {
        setOwner(key || "");
        if (key) setAttendees((current) => (current.includes(key) ? current : [key, ...current]));
    };
    const remove = (key) => setAttendees((current) => current.filter((item) => item !== key));

    const submit = async () => {
        setError("");
        if (!title.trim()) return setError("عنوان جلسه را بنویسید.");
        if (!date) return setError("تاریخ جلسه را انتخاب کنید.");
        if (start >= end) return setError("ساعت پایان باید بعد از ساعت شروع باشد.");
        if (!owner) return setError("مسئول جلسه را انتخاب کنید.");
        if (!attendees.length) return setError("برای جلسه دست‌کم یک عضو را دعوت کنید.");
        setSaving(true);
        try {
            await onSave({title: title.trim(), date, start, end, location: location.trim(), owner, attendees, agenda: agenda.map((item) => item.trim()).filter(Boolean)});
        } catch (requestError) {
            setError(errorText(requestError));
            setSaving(false);
        }
    };

    return (
        <Dialog visible={visible} onHide={onHide} modal dir="rtl" className="rtl w-11 md:w-8 lg:w-6 xl:w-5 mt-meeting-dialog"
                header={(
                    <div className="flex align-items-center gap-3">
                        <span className="mt-head-icon"><Icon icon="mi mi:event"/></span>
                        <div>
                            <b className="text-base">{meeting ? "ویرایش جلسه" : "ایجاد جلسه"}</b>
                            <p className="pd-sub">{meeting ? "زمان، اعضا یا دستور جلسه را اصلاح کنید." : "زمان و افراد را مشخص کنید؛ دستور جلسه را هم می‌توانید اضافه کنید."}</p>
                        </div>
                    </div>
                )}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <button type="button" className="pd-btn" onClick={onHide}>انصراف</button>
                        <button type="button" className="pd-btn primary" disabled={saving} onClick={submit}>
                            <Icon icon="mi mi:event_available" className="text-sm"/>{saving ? "در حال ذخیره…" : meeting ? "ذخیره تغییرات جلسه" : "ایجاد جلسه"}
                        </button>
                    </div>
                )}>
            <div className="flex flex-column gap-3">
                <label className="mt-field"><span>عنوان جلسه <b className="tone-red">*</b></span>
                    <InputText autoFocus value={title} onChange={(event) => setTitle(event.target.value)} placeholder="مثلاً بررسی پیشرفت پروژه" className="w-full text-xs" maxLength={200}/>
                </label>
                <div className="mt-form-grid three">
                    <label className="mt-field"><span>تاریخ <b className="tone-red">*</b></span>
                        <DayPicker value={date} onChange={setDate}/>
                    </label>
                    <label className="mt-field"><span>ساعت شروع <b className="tone-red">*</b></span>
                        <Dropdown value={start} options={TIME_OPTIONS} onChange={(event) => setStart(event.value)} className="w-full text-xs" panelClassName="rtl text-xs" scrollHeight="14rem"/>
                    </label>
                    <label className="mt-field"><span>ساعت پایان <b className="tone-red">*</b></span>
                        <Dropdown value={end} options={TIME_OPTIONS} onChange={(event) => setEnd(event.value)} className="w-full text-xs" panelClassName="rtl text-xs" scrollHeight="14rem"/>
                    </label>
                </div>
                <div className="mt-form-grid">
                    <label className="mt-field">محل یا لینک جلسه
                        <InputText value={location} onChange={(event) => setLocation(event.target.value)} placeholder="مثلاً اتاق جلسات یا https://meet…" className="w-full text-xs" maxLength={300}/>
                    </label>
                    <label className="mt-field"><span>مسئول جلسه <b className="tone-red">*</b></span>
                        <Dropdown value={owner || null} options={everyone} optionLabel="name" optionValue="key" onChange={(event) => chooseOwner(event.value)} filter filterBy="name"
                                  placeholder="انتخاب مسئول" itemTemplate={personItem} valueTemplate={(option, props) => (option ? personItem(option) : <span>{props.placeholder}</span>)}
                                  className="w-full text-xs" panelClassName="rtl text-xs" emptyFilterMessage="کسی پیدا نشد"/>
                    </label>
                </div>

                <section className="mt-form-section">
                    <div className="mt-form-section-head">
                        <div>
                            <b className="text-xs"><Icon icon="mi mi:group" className="text-sm"/> اعضای دعوت‌شده</b>
                            <span className="pd-tiny text-500">{`${faNum(attendees.length)} نفر · هر عضو فضای کاری را می‌توانید جست‌وجو و دعوت کنید`}</span>
                        </div>
                        <div className="flex gap-2">
                            {(people || []).length > 0 && <button type="button" className="pd-link pd-tiny" onClick={() => setAttendees((current) => [...new Set([...current, ...people.map((item) => item.key)])])}>همه‌ی اعضای پروژه</button>}
                            <button type="button" className="pd-link pd-tiny" onClick={() => setAttendees(owner ? [owner] : [])}>پاک‌کردن</button>
                        </div>
                    </div>
                    <Dropdown value={null} options={candidates} optionLabel="name" optionValue="key" onChange={(event) => event.value && setAttendees((current) => [...current, event.value])}
                              filter filterBy="name" placeholder="جست‌وجو و افزودن فرد…" itemTemplate={(option) => (
                                  <span className="flex align-items-center justify-content-between gap-2 w-full">
                                      {personItem(option)}
                                      {tagOutside && !projectKeys.has(option.key) && <span className="pd-pill tone-gray">خارج از پروژه</span>}
                                  </span>
                              )}
                              className="w-full text-xs" panelClassName="rtl text-xs" emptyMessage="همه دعوت شده‌اند" emptyFilterMessage="کسی پیدا نشد"/>
                    <div className="mt-chips">
                        {attendees.map((key) => {
                            const person = byKey.get(key);
                            return (
                                <span key={key} className={`mt-chip ${key === owner ? "is-owner" : ""}`}>
                                    {person?.member && <Avatar memberData={person.member} size="normal"/>}
                                    <span className="text-xs">{person?.name || "عضو سابق"}</span>
                                    {key === owner && <span className="pd-pill tone-blue">مسئول</span>}
                                    {tagOutside && key !== owner && !projectKeys.has(key) && <span className="pd-pill tone-gray">خارج از پروژه</span>}
                                    {key !== owner && <button type="button" className="mt-chip-remove" aria-label={`حذف ${person?.name || ""}`} onClick={() => remove(key)}><Icon icon="mi mi:close" className="text-xs"/></button>}
                                </span>
                            );
                        })}
                    </div>
                </section>

                <section className="mt-form-section">
                    <div className="mt-form-section-head">
                        <div>
                            <b className="text-xs"><Icon icon="mi mi:format_list_numbered" className="text-sm"/> دستور جلسه</b>
                            <span className="pd-tiny text-500">موضوع‌ها را ویرایش کنید یا مورد تازه بیفزایید.</span>
                        </div>
                    </div>
                    <div className="flex flex-column gap-2" ref={agendaRef}>
                        {agenda.map((topic, index) => (
                            <div key={index} className="flex align-items-center gap-2">
                                <span className="mt-index">{faNum(index + 1)}</span>
                                <InputText value={topic} data-topic={index} aria-label={`موضوع ${index + 1}`} placeholder="موضوع دستور جلسه" className="flex-1 text-xs" maxLength={300}
                                           onChange={(event) => setAgenda((current) => current.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)))}
                                           onKeyDown={(event) => {
                                               if (event.key === "Enter") {
                                                   event.preventDefault();
                                                   addTopic(index);
                                               }
                                           }}/>
                                <button type="button" className="mt-icon-btn" aria-label="حذف موضوع" title="حذف موضوع" onClick={() => setAgenda((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                                    <Icon icon="mi mi:delete" className="text-sm"/>
                                </button>
                            </div>
                        ))}
                    </div>
                    <button type="button" className="pd-btn small align-self-start" onClick={() => addTopic()}>
                        <Icon icon="mi mi:add" className="text-sm"/>افزودن موضوع
                    </button>
                </section>

                {meeting && <p className="pd-tiny text-500 m-0">حضور اعضایی که هنوز دعوت هستند حفظ می‌شود؛ عضو حذف‌شده از فهرست حضور هم حذف می‌شود.</p>}
                {error && <p className="text-xs tone-red m-0" role="alert">{error}</p>}
            </div>
        </Dialog>
    );
}
